import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, type UserResponse } from '@services/users';
import AdminAnalyticsNav from '@components/admin/AdminAnalyticsNav';
import {
	getAdminUserActivity,
	isUserActivityMatchResponse,
	type UserActivityMatch,
	type UserActivityResponse
} from '@services/adminActivity';
import { downloadUserActivityExcel } from '@utils/exportUserActivityExcel';

function formatDuration(seconds: number | null | undefined): string {
	if (seconds == null || seconds <= 0) return '0m';
	const total = Math.round(seconds);
	const hours = Math.floor(total / 3600);
	const minutes = Math.floor((total % 3600) / 60);
	if (hours > 0) return `${hours}h ${minutes}m`;
	return `${minutes}m`;
}

function formatDateTime(value: string | null | undefined): string {
	if (!value) return '—';
	return new Date(value).toLocaleString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
}

function formatDay(value: string): string {
	return new Date(value).toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric'
	});
}

function userLabel(u: Pick<UserResponse, 'name' | 'email' | 'role' | 'company_name'>): string {
	const parts = [u.name || u.email];
	if (u.company_name) parts.push(u.company_name);
	parts.push(`(${u.role})`);
	return parts.join(' · ');
}

function daysSince(value: string | null | undefined): number | null {
	if (!value) return null;
	const then = new Date(value);
	if (Number.isNaN(then.getTime())) return null;
	return Math.max(0, Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24)));
}

function percentLabel(numerator: number, denominator: number): string {
	if (denominator <= 0) return '—';
	return `${Math.round((numerator / denominator) * 100)}%`;
}

export default function AdminUserActivity() {
	const [users, setUsers] = useState<UserResponse[]>([]);
	const [search, setSearch] = useState('');
	const [days, setDays] = useState(90);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [matches, setMatches] = useState<UserActivityMatch[] | null>(null);
	const [report, setReport] = useState<UserActivityResponse | null>(null);

	useEffect(() => {
		getUsers()
			.then((list) => setUsers(Array.isArray(list) ? list : []))
			.catch(() => setUsers([]));
	}, []);

	const userOptions = useMemo(() => {
		const q = search.trim().toLowerCase();
		let list = users;
		if (q) {
			list = users.filter(
				(u) =>
					String(u.name || '')
						.toLowerCase()
						.includes(q) ||
					String(u.email || '')
						.toLowerCase()
						.includes(q) ||
					String(u.company_name || '')
						.toLowerCase()
						.includes(q)
			);
		}
		return [...list].sort((a, b) =>
			String(a.name || a.email).localeCompare(String(b.name || b.email))
		);
	}, [users, search]);

	const loadReport = useCallback(async (opts: { userId?: number; q?: string }) => {
		setLoading(true);
		setError(null);
		setMatches(null);
		try {
			const data = await getAdminUserActivity({ ...opts, days });
			if (isUserActivityMatchResponse(data)) {
				setReport(null);
				setMatches(data.matches);
				if (data.matches.length === 0) {
					setError('No users matched that search.');
				}
				return;
			}
			setReport(data);
			setMatches(null);
		} catch (e) {
			setReport(null);
			setMatches(null);
			setError(e instanceof Error ? e.message : 'Failed to load user activity');
		} finally {
			setLoading(false);
		}
	}, [days]);

	function handleSearchSubmit(e: React.FormEvent) {
		e.preventDefault();
		const q = search.trim();
		if (!q) {
			setError('Enter a name or email to search.');
			return;
		}
		void loadReport({ q });
	}

	function pickUser(u: UserActivityMatch | UserResponse) {
		setSearch(u.name || u.email);
		void loadReport({ userId: u.id });
	}

	const activity = report?.activity;
	const engagedMinutes = activity ? Math.round(activity.engaged_seconds / 60) : 0;
	const daysSinceLastSeen = daysSince(activity?.last_seen_at);
	const questionnaireCompletion = percentLabel(
		activity?.questionnaire_submits ?? 0,
		activity?.questionnaire_starts ?? 0
	);
	const generationSuccess = percentLabel(
		activity?.plan_generation_successes ?? 0,
		activity?.plan_generation_starts ?? 0
	);
	const avgSessionMinutes =
		activity && activity.total_sessions > 0
			? Math.round((activity.engaged_seconds / activity.total_sessions / 60) * 10) / 10
			: null;

	return (
		<>
			<div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
				<div>
					<h1 className="h3 mb-1">User activity</h1>
					<p className="text-muted small mb-0">
						See how much someone has used the app — time in app, plans, projects, and events.{' '}
						<Link to="/app/admin/analytics" className="text-decoration-none" style={{ color: '#2f9e59' }}>
							← Admin Insights
						</Link>
					</p>
				</div>
				<div className="d-flex align-items-center gap-2">
					<label className="text-muted small mb-0">Window</label>
					<select
						className="form-select form-select-sm"
						value={days}
						onChange={(e) => {
							const nextDays = Number(e.target.value);
							setDays(nextDays);
							if (report?.user.id) {
								void getAdminUserActivity({ userId: report.user.id, days: nextDays })
									.then((data) => {
										if (!isUserActivityMatchResponse(data)) setReport(data);
									})
									.catch(() => {});
							}
						}}
						style={{ width: 130 }}
					>
						<option value={7}>Last 7 days</option>
						<option value={30}>Last 30 days</option>
						<option value={90}>Last 90 days</option>
						<option value={365}>Last year</option>
						<option value={0}>All time</option>
					</select>
				</div>
			</div>

			<AdminAnalyticsNav />

			<div className="card mb-4">
				<div className="card-body">
					<form className="row g-2 align-items-end" onSubmit={handleSearchSubmit}>
						<div className="col-12 col-lg-6">
							<label className="form-label small text-muted mb-1">Search by name or email</label>
							<input
								type="search"
								className="form-control"
								placeholder="e.g. Jan, jan@…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								list="admin-user-activity-options"
							/>
							<datalist id="admin-user-activity-options">
								{userOptions.slice(0, 50).map((u) => (
									<option key={u.id} value={u.name || u.email}>
										{userLabel(u)}
									</option>
								))}
							</datalist>
						</div>
						<div className="col-auto">
							<button type="submit" className="btn btn-success" disabled={loading}>
								{loading ? 'Loading…' : 'Look up'}
							</button>
						</div>
					</form>

					{userOptions.length > 0 && search.trim() === '' && (
						<div className="mt-3">
							<div className="text-muted small mb-2">Quick pick</div>
							<div className="d-flex flex-wrap gap-2">
								{userOptions.slice(0, 12).map((u) => (
									<button
										key={u.id}
										type="button"
										className="btn btn-sm btn-outline-secondary"
										onClick={() => pickUser(u)}
										disabled={loading}
									>
										{u.name || u.email}
									</button>
								))}
							</div>
						</div>
					)}

					{error && <div className="alert alert-warning mt-3 mb-0 py-2 small">{error}</div>}

					{matches && matches.length > 1 && (
						<div className="mt-3">
							<div className="text-muted small mb-2">Multiple users matched — pick one:</div>
							<div className="list-group">
								{matches.map((u) => (
									<button
										key={u.id}
										type="button"
										className="list-group-item list-group-item-action text-start"
										onClick={() => pickUser(u)}
										disabled={loading}
									>
										<div className="fw-medium">{u.name}</div>
										<div className="small text-muted">
											{u.email} · {u.role}
											{u.company_name ? ` · ${u.company_name}` : ''}
										</div>
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			{report && (
				<>
					<div className="card mb-3 border-top border-4 border-success-subtle">
						<div className="card-body">
							<div className="d-flex flex-wrap justify-content-between gap-2">
								<div>
									<h2 className="h4 mb-1">{report.user.name}</h2>
									<div className="text-muted small">
										{report.user.email} · {report.user.role}
										{report.user.company_name ? ` · ${report.user.company_name}` : ''}
									</div>
									<div className="text-muted small mt-1">
										Account created {formatDateTime(report.user.created_at)}
									</div>
								</div>
								<div className="text-end">
									<div className="text-muted small">Last seen (logged-in activity)</div>
									<div className="fw-semibold">{formatDateTime(activity?.last_seen_at)}</div>
									<div className="text-muted small mt-1">
										{daysSinceLastSeen != null ? `${daysSinceLastSeen} days ago` : 'No tracked activity'}
									</div>
									<div className="text-muted small mt-1">
										First tracked {formatDateTime(activity?.first_seen_at)}
									</div>
									<button
										type="button"
										className="btn btn-sm btn-outline-success mt-3"
										onClick={() => report && downloadUserActivityExcel(report, days)}
									>
										<i className="bi bi-file-earmark-spreadsheet me-1" />
										Download Excel
									</button>
								</div>
							</div>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-6 col-md-3">
							<div className="card h-100 border-0 bg-body-tertiary">
								<div className="card-body py-3">
									<div className="text-muted small">Questionnaire completion</div>
									<div className="fw-semibold">{questionnaireCompletion}</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-3">
							<div className="card h-100 border-0 bg-body-tertiary">
								<div className="card-body py-3">
									<div className="text-muted small">Generation success rate</div>
									<div className="fw-semibold">{generationSuccess}</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-3">
							<div className="card h-100 border-0 bg-body-tertiary">
								<div className="card-body py-3">
									<div className="text-muted small">Avg session length</div>
									<div className="fw-semibold">
										{avgSessionMinutes != null ? `${avgSessionMinutes} min` : '—'}
									</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-3">
							<div className="card h-100 border-0 bg-body-tertiary">
								<div className="card-body py-3">
									<div className="text-muted small">Total events</div>
									<div className="fw-semibold">{activity?.total_events ?? 0}</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Time in app</div>
									<div className="fs-3 fw-semibold">{formatDuration(activity?.engaged_seconds)}</div>
									<div className="text-muted small">~{engagedMinutes} min (heartbeats)</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Active days</div>
									<div className="fs-3 fw-semibold">{activity?.active_days ?? 0}</div>
									<div className="text-muted small">{activity?.total_sessions ?? 0} sessions</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Plans (all)</div>
									<div className="fs-3 fw-semibold">{report.plans.total}</div>
									<div className="text-muted small">
										{report.plans.draft} draft · {report.plans.published} published
									</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Projects</div>
									<div className="fs-3 fw-semibold">{report.projects.total}</div>
									<div className="text-muted small">
										{report.projects.in_progress} in progress · {report.projects.completed} done
									</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Plan generations</div>
									<div className="fs-3 fw-semibold">{activity?.plan_generation_successes ?? 0}</div>
									<div className="text-muted small">
										{activity?.plan_generation_errors ?? 0} errors · {activity?.plan_generation_starts ?? 0} starts
									</div>
								</div>
							</div>
						</div>
						<div className="col-6 col-md-4 col-xl-2">
							<div className="card h-100">
								<div className="card-body">
									<div className="text-muted small text-uppercase">Questionnaires</div>
									<div className="fs-3 fw-semibold">{activity?.questionnaire_submits ?? 0}</div>
									<div className="text-muted small">
										{activity?.questionnaire_starts ?? 0} starts · {activity?.login_successes ?? 0} logins
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-12 col-xl-8">
							<div className="card h-100">
								<div className="card-header">Activity by day</div>
								<div className="card-body">
									{report.daily_activity.length === 0 ? (
										<p className="text-muted small mb-0">No logged-in activity in this window.</p>
									) : (
										<div className="table-responsive">
											<table className="table table-sm align-middle mb-0">
												<thead className="text-muted small text-uppercase">
													<tr>
														<th>Day</th>
														<th className="text-end">Time</th>
														<th className="text-end">Sessions</th>
														<th className="text-end">Events</th>
													</tr>
												</thead>
												<tbody>
													{[...report.daily_activity].reverse().map((row) => (
														<tr key={row.day}>
															<td>{formatDay(row.day)}</td>
															<td className="text-end">{formatDuration(Number(row.engaged_seconds))}</td>
															<td className="text-end">{row.sessions}</td>
															<td className="text-end">{row.events}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="col-12 col-xl-4">
							<div className="card h-100">
								<div className="card-header">Device & browser</div>
								<div className="card-body">
									<div className="small text-muted text-uppercase mb-1">Devices</div>
									<ul className="list-group list-group-flush mb-3">
										{report.devices.map((row) => (
											<li key={row.device_type} className="list-group-item px-0 d-flex justify-content-between">
												<span>{row.device_type}</span>
												<span>{row.events}</span>
											</li>
										))}
										{report.devices.length === 0 && (
											<li className="list-group-item px-0 text-muted small">No data</li>
										)}
									</ul>
									<div className="small text-muted text-uppercase mb-1">Browsers</div>
									<ul className="list-group list-group-flush">
										{report.browsers.map((row) => (
											<li key={row.browser} className="list-group-item px-0 d-flex justify-content-between">
												<span>{row.browser}</span>
												<span>{row.events}</span>
											</li>
										))}
										{report.browsers.length === 0 && (
											<li className="list-group-item px-0 text-muted small">No data</li>
										)}
									</ul>
								</div>
							</div>
						</div>
					</div>

					<div className="row g-3 mb-3">
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header">Plans ({report.plans_list.length})</div>
								<div className="card-body">
									{report.plans_list.length === 0 ? (
										<p className="text-muted small mb-0">No plans in the database for this user.</p>
									) : (
										<div className="table-responsive">
											<table className="table table-sm align-middle mb-0">
												<thead className="text-muted small text-uppercase">
													<tr>
														<th>Title</th>
														<th>Status</th>
														<th className="text-end">Gen time</th>
														<th className="text-end">Created</th>
													</tr>
												</thead>
												<tbody>
													{report.plans_list.map((plan) => (
														<tr key={plan.id}>
															<td className="text-break">
																<Link
																	to={`/app/seeker/plan/${plan.id}`}
																	className="text-decoration-none fw-medium"
																	style={{ color: '#2f9e59' }}
																>
																	{plan.title}
																</Link>
															</td>
															<td>
																<span className="badge text-bg-light border">{plan.status}</span>
																{plan.moderation_status && plan.moderation_status !== 'visible' && (
																	<span className="badge text-bg-secondary ms-1">{plan.moderation_status}</span>
																)}
															</td>
															<td className="text-end">
																{plan.generation_duration_seconds != null
																	? formatDuration(plan.generation_duration_seconds)
																	: '—'}
															</td>
															<td className="text-end text-muted small">{formatDay(plan.created_at)}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
									{(report.plans.hidden > 0 || report.plans.deleted > 0) && (
										<p className="text-muted small mt-2 mb-0">
											Includes {report.plans.hidden} hidden and {report.plans.deleted} deleted (moderation).
										</p>
									)}
								</div>
							</div>
						</div>
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header">Recent events</div>
								<div className="card-body" style={{ maxHeight: 360, overflowY: 'auto' }}>
									{report.recent_events.length === 0 ? (
										<p className="text-muted small mb-0">No events recorded while logged in.</p>
									) : (
										<ul className="list-group list-group-flush">
											{report.recent_events.map((event, index) => (
												<li key={`${event.created_at}-${index}`} className="list-group-item px-0">
													<div className="d-flex justify-content-between gap-2">
														<span className="fw-medium small">{event.event_name}</span>
														<span className="text-muted small text-nowrap">
															{formatDateTime(event.created_at)}
														</span>
													</div>
													<div className="text-muted small text-break">{event.page_path || '—'}</div>
													{(event.device_type || event.browser) && (
														<div className="text-muted small">
															{[event.device_type, event.browser].filter(Boolean).join(' · ')}
														</div>
													)}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-header">Offers & friction</div>
						<div className="card-body">
							<div className="row g-3">
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Offers accepted (on plans)</div>
										<div className="fs-4 fw-semibold">{report.offers.received_by_status?.accepted ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Offers pending (on plans)</div>
										<div className="fs-4 fw-semibold">{report.offers.received_by_status?.pending ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Validation errors</div>
										<div className="fs-4 fw-semibold">{activity?.questionnaire_validation_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Generation errors</div>
										<div className="fs-4 fw-semibold">{activity?.plan_generation_errors ?? 0}</div>
									</div>
								</div>
							</div>
							<div className="row g-3 mt-1">
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Offers on their plans (total)</div>
										<div className="fs-4 fw-semibold">{report.offers.received_on_plans}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Offers sent (gardener)</div>
										<div className="fs-4 fw-semibold">{report.offers.sent_as_gardener}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Upload errors</div>
										<div className="fs-4 fw-semibold">{activity?.questionnaire_upload_errors ?? 0}</div>
									</div>
								</div>
								<div className="col-6 col-md-3">
									<div className="border rounded p-3 text-center h-100">
										<div className="text-muted small">Offers rejected (on plans)</div>
										<div className="fs-4 fw-semibold">{report.offers.received_by_status?.rejected ?? 0}</div>
									</div>
								</div>
							</div>
							<p className="text-muted small mt-3 mb-0">
								Time in app counts logged-in heartbeats only (tab visible). Anonymous browsing before login is not
								attributed to this account.
							</p>
						</div>
					</div>
				</>
			)}
		</>
	);
}
